/* global maplibregl */

import {
  amountOfNewProperties,
  clusterShowAvailableBiggerRadius,
  clusterShowAvailableBiggerStroke,
  hasAvailableResidences,
  hasNewAndAvailableResidences,
  hasNewResidences,
  isNewBuild,
  isRented,
  propertiesClusterCountLayer,
  propertiesClusterLayer,
  propertiesClusterSource,
  propertiesUnclusteredCircleLayer,
  propertiesUnclusteredCircleLayerLabel,
  singlePointShowAvailableBiggerRadius,
  singlePointShowAvailableBiggerStroke,
  sumOfAvailableResidences,
} from "./modules/cluster_filters.js";

import { circleColors, circleBlurColors } from "./modules/colors.js";
import {
  changeCursorOnHovertest,
  getPropertiesInView,
} from "./modules/map_event_handlers.js";

import {
  filterPropertiesList,
  mapToggleListButton,
  mapToggleListWrapper,
} from "./modules/property_list.js";

import { populatePopup } from "./modules/popup.js";
import { updateAreaNavigation, getAreaInView } from "./modules/areas.js";

(function ($, Drupal) {
  /**
   * Marker.
   *
   * @type {Drupal~behavior}
   *
   * @prop {Drupal~behaviorAttach} attach
   *   Attaches common map style functionality to relevant elements.
   */
  Drupal.behaviors.geolocationMarkerClusterer = {
    attach: function (context, drupalSettings) {
      // Global variables.

      let clusterFeatures;

      Drupal.geolocation.executeFeatureOnAllMaps(
        "viamap_marker_clusterer",
        /**
         * @param {GeolocationMapInterface} map
         * @param {GeolocationMapFeatureSettings} featureSettings
         */
        // eslint-disable-next-line
        function (map, featureSettings) {
          const maplibreMap = map.viamap;

          map.addPopulatedCallback(function (map) {
            clusterFeatures = featureSettings.clusterFeatures;

            maplibreMap.addSource(propertiesClusterSource, {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: clusterFeatures,
              },
              cluster: true,
              clusterMaxZoom: 14,
              // Max zoom to cluster points on
              clusterRadius: 50,
              // Radius of each cluster when clustering points (defaults to 50)
              clusterProperties: {
                sumOfAvailableResidences: sumOfAvailableResidences,
                amountOfNewProperties: amountOfNewProperties,
              },
            });

            // A single point that is _not_ in a cluster.
            maplibreMap.addLayer({
              id: propertiesUnclusteredCircleLayer,
              source: propertiesClusterSource,
              filter: ["!", ["has", "point_count"]],
              type: "circle",
              paint: {
                "circle-color": [
                  "case",
                  hasNewAndAvailableResidences,
                  circleColors.available,
                  hasNewResidences,
                  circleColors.newBuild,
                  hasAvailableResidences,
                  circleColors.available,
                  circleColors.rented,
                ],
                "circle-radius": singlePointShowAvailableBiggerRadius,
                "circle-stroke-color": [
                  "case",
                  hasNewAndAvailableResidences,
                  circleBlurColors.available,
                  hasNewResidences,
                  circleBlurColors.newBuild,
                  hasAvailableResidences,
                  circleBlurColors.available,
                  circleBlurColors.rented,
                ],
                "circle-stroke-width": singlePointShowAvailableBiggerStroke,
                "circle-blur": 0,
              },
            });

            // Label for a single point that is _not_ in a cluster.
            maplibreMap.addLayer({
              id: propertiesUnclusteredCircleLayerLabel,
              source: propertiesClusterSource,
              filter: ["!", ["has", "point_count"]],
              type: "symbol",
              layout: {
                "text-field": "{property_amount_of_available_residences}",
                "text-font": ["Noto Sans Regular"],
                // We want our own custom font, right?
                "text-size": 14,
              },
              paint: {
                "text-color": "#fff",
              },
            });
            maplibreMap.addLayer({
              id: propertiesClusterLayer,
              type: "circle",
              source: propertiesClusterSource,
              filter: [
                "all",
                ["has", "point_count"],
                ["has", "sumOfAvailableResidences"],
              ],
              paint: {
                "circle-color": [
                  "case",
                  isNewBuild,
                  circleColors.newBuild,
                  isRented,
                  circleColors.rented,
                  circleColors.available,
                ],
                "circle-radius": clusterShowAvailableBiggerRadius,
                "circle-stroke-color": [
                  "case",
                  isNewBuild,
                  circleBlurColors.newBuild,
                  isRented,
                  circleBlurColors.rented,
                  circleBlurColors.available,
                ],
                "circle-stroke-width": clusterShowAvailableBiggerStroke,
                "circle-blur": 0,
              },
            });
            maplibreMap.addLayer({
              id: propertiesClusterCountLayer,
              type: "symbol",
              source: propertiesClusterSource,
              filter: [
                "all",
                ["has", "point_count"],
                ["has", "sumOfAvailableResidences"],
              ],
              layout: {
                "text-field": "{sumOfAvailableResidences}",
                "text-font": ["Noto Sans Regular"],
                // We want our own custom font, right?
                "text-size": 14,
              },
              paint: {
                "text-color": "#fff",
              },
            });

            // Prepare an array that we can use to fit our map to our cluster
            // features.
            let featuresArray = [];
            featureSettings.clusterFeatures.forEach(function (feature) {
              featuresArray.push(feature.geometry.coordinates);
            });

            // Please see below link for explanation.
            // https://maplibre.org/maplibre-gl-js-docs/example/zoomto-linestring/
            const bounds = featuresArray.reduce(function (bounds, coord) {
              return bounds.extend(coord);
            }, new maplibregl.LngLatBounds(featuresArray[0], featuresArray[0]));
            map.lat = bounds.getCenter().lat;
            map.lng = bounds.getCenter().lng;
            maplibreMap.setCenter(bounds.getCenter().toArray());

            const interactiveLayers = [
              propertiesUnclusteredCircleLayer,
              propertiesClusterCountLayer,
            ];

            interactiveLayers.forEach((layer) =>
              changeCursorOnHovertest(maplibreMap, layer)
            );

            const mapMobileIntroHeader = document.querySelector(
              ".map-properties__map .map__header"
            );

            maplibreMap.on("zoomend", function () {
              const currentZoom = maplibreMap.getZoom();
              const popup = document.querySelector(".map-popup__outer-wrapper");
              if (currentZoom < 10 && popup) {
                popup.remove();
              }
            });

            if (mapToggleListButton) {
              mapToggleListButton.addEventListener("click", () => {
                if (mapToggleListButton.classList.contains("list-is-open")) {
                  mapToggleListButton.classList.remove("list-is-open");
                  mapToggleListWrapper.classList.remove("is-open");
                  mapToggleListButton.textContent = Drupal.t("Show list");
                } else if (
                  !mapToggleListButton.classList.contains("list-is-open")
                ) {
                  mapToggleListButton.classList.add("list-is-open");
                  mapToggleListWrapper.classList.add("is-open");
                  mapToggleListButton.textContent = Drupal.t("Show map");
                }
              });
            }

            const maplibremapCanvas = maplibreMap.getCanvasContainer();

            maplibremapCanvas.addEventListener("click", () => {
              if (
                mapMobileIntroHeader &&
                !mapMobileIntroHeader.classList.contains("fade")
              ) {
                mapMobileIntroHeader.classList.add("fade");
              }
            });

            // inspect a cluster on click
            maplibreMap.on("click", propertiesClusterLayer, function (e) {
              const features = maplibreMap.queryRenderedFeatures(e.point, {
                layers: [propertiesClusterLayer],
              });
              const coordinates = features[0].geometry.coordinates;
              const clusterId = features[0].properties.cluster_id;
              maplibreMap
                .getSource(propertiesClusterSource)
                .getClusterExpansionZoom(clusterId, function (err, zoom) {
                  if (err) {
                    console.error(err);
                    return;
                  }

                  maplibreMap.easeTo({
                    center: coordinates,
                    zoom: zoom,
                  });
                });
            });

            // When a click event occurs on a feature in
            // the unclustered point layer, open a popup at
            // the location of the feature, with description HTML from its
            // properties.
            maplibreMap.on(
              "click",
              propertiesUnclusteredCircleLayer,
              function (e) {
                const popup = new maplibregl.Popup({
                  className: "map-popup__outer-wrapper",
                  maxWidth: "450px",
                  closeOnClick: true,
                  closeButton: true,
                  offset: {
                    bottom: [0, -25],
                  },
                });
                const coordinates = e.features[0].geometry.coordinates.slice();
                const properties = e.features[0].properties;
                const popup_content = populatePopup(properties);
                popup.setLngLat(coordinates);
                popup.setHTML(popup_content);
                popup.addClassName("map-property-marker-infobox");
                popup.addTo(maplibreMap);
                maplibreMap.easeTo({
                  center: coordinates,
                  // If the zoom level is greater or equal to the arbitrary
                  // amount we set, zoom to the same level as the current zoom.
                  // If not, we zoom to our arbitrary amount that fits our needs.
                  // The higher the number, the closer to the ground. Max 20.
                  zoom:
                    maplibreMap.getZoom() >= 14 ? maplibreMap.getZoom() : 14,
                });
              }
            );

            maplibreMap.on("moveend", async function () {
              const features = maplibreMap.queryRenderedFeatures({
                // Limit our query to only the relevant layers.
                layers: interactiveLayers,
              });
              const featuresInMap = await getPropertiesInView(
                features,
                maplibreMap
              );
              const singleAreaInView = getAreaInView(featuresInMap);

              filterPropertiesList(featuresInMap);

              updateAreaNavigation(singleAreaInView);
            });
          });
          return true;
        },
        drupalSettings
      );
      /**
       * Change the cursor when hovering a layer.
       *
       * @param map - The maplibre map.
       * @param layer - The target layer.
       */

      /**
       * Defines all operations that should be executed on move-end.
       * This function is async, in order to await getPropertiesInView.
       * The correct amount of properties in the view from getPropertiesInView
       * returns a promise for each property, ensuring a correct amount.
       *
       * @param maplibreMap - The maplibre map.
       * @param layers - The target layer.
       */
    },
    // eslint-disable-next-line
    detach: function (context, drupalSettings) {},
  };
})(jQuery, Drupal);
